import React from 'react';
import Modal from './Modal';
import Badge from './Badge';
import { theme } from '../../theme';
import { getFileUrl } from '../../services/api';

function ClaimDetailModal({ claim, onClose }) {
  if (!claim) return null;

  const items = claim.items || [];
  const history = claim.history || [];
  const submittedTotal = items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const approvedTotal = items.reduce((s, i) => s + Number(i.approved_amount ?? i.amount ?? 0), 0);
  const hasApproved = items.some((i) => i.approved_amount !== null && i.approved_amount !== undefined);

  const rowStyle = { padding: '0.6rem 0.75rem', color: theme.text, fontSize: '0.8rem' };

  return (
    <Modal isOpen={!!claim} onClose={onClose} title={`${claim.claim_code || `Claim #${claim.id}`} — ${claim.title}`} maxWidth="700px">

      {/* Rejection banner */}
      {claim.status === 'rejected' && claim.finance_remarks && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '0.5rem', padding: '0.875rem 1rem', marginBottom: '1.25rem', display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>❌</span>
          <div>
            <p style={{ fontWeight: 700, fontSize: '0.875rem', color: '#DC2626', margin: '0 0 0.25rem' }}>Claim Rejected</p>
            <p style={{ fontSize: '0.85rem', color: '#7F1D1D', margin: 0, lineHeight: 1.5 }}>
              <strong>Reason:</strong> {claim.finance_remarks}
            </p>
          </div>
        </div>
      )}

      {/* Meta */}
      <div style={{ background: theme.pageBg, borderRadius: '0.5rem', padding: '0.875rem 1rem', marginBottom: '1.25rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1.5rem' }}>
        {[
          ['Employee', claim.employee_name],
          ['Project', claim.project_name || '—'],
          ['Submitted', claim.submitted_at ? new Date(claim.submitted_at).toLocaleDateString() : '—'],
          ['Status', <Badge status={claim.status} />],
          claim.finance_remarks && ['Finance Remarks', claim.finance_remarks],
        ].filter(Boolean).map(([label, val]) => (
          <div key={label}>
            <span style={{ fontSize: '0.7rem', color: theme.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>{label}</span>
            <div style={{ marginTop: '0.15rem', fontWeight: 600, fontSize: '0.875rem' }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Items table */}
      <p style={{ fontSize: '0.75rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Expense Items</p>
      <div style={{ border: `1px solid ${theme.border}`, borderRadius: '0.5rem', overflow: 'hidden', marginBottom: '1.25rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
          <thead>
            <tr style={{ background: '#F8FAFC' }}>
              {['Description', 'Category', 'Date', 'Submitted (₹)', hasApproved && 'Approved (₹)', 'Receipt'].filter(Boolean).map((h) => (
                <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600, color: theme.textMuted, borderBottom: `1px solid ${theme.border}`, whiteSpace: 'nowrap', fontSize: '0.7rem', textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: '1.5rem', color: theme.textMuted }}>No items</td></tr>
            ) : items.map((item) => (
              <tr key={item.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                <td style={rowStyle}>{item.description}</td>
                <td style={{ ...rowStyle, color: theme.textMuted, textTransform: 'capitalize' }}>{item.category}</td>
                <td style={{ ...rowStyle, whiteSpace: 'nowrap', color: theme.textMuted }}>{item.expense_date ? new Date(item.expense_date + 'T12:00:00').toLocaleDateString() : '—'}</td>
                <td style={{ ...rowStyle, fontWeight: 600 }}>₹{Number(item.amount || 0).toLocaleString()}</td>
                {hasApproved && (
                  <td style={{ ...rowStyle, fontWeight: 700, color: item.approved_amount !== null && item.approved_amount !== undefined ? theme.success : theme.textMuted }}>
                    {item.approved_amount !== null && item.approved_amount !== undefined
                      ? `₹${Number(item.approved_amount).toLocaleString()}`
                      : '—'}
                  </td>
                )}
                <td style={rowStyle}>
                  {item.bill_available === false ? (
                    <span style={{ fontSize: '0.68rem', fontWeight: 700, background: '#FEF3C7', color: '#92400E', borderRadius: '0.25rem', padding: '0.15rem 0.45rem', border: '1px solid #FDE68A' }}>No Bill</span>
                  ) : item.bill_file_path ? (
                    <a href={getFileUrl(item.bill_file_path)} target="_blank" rel="noreferrer"
                      style={{ color: theme.primary, fontWeight: 600, fontSize: '0.75rem', textDecoration: 'none' }}>
                      📎 View
                    </a>
                  ) : <span style={{ color: theme.textMuted }}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, background: theme.pageBg, borderRadius: '0.5rem', padding: '0.75rem 1rem', border: `1px solid ${theme.border}` }}>
          <p style={{ fontSize: '0.7rem', color: theme.textMuted, fontWeight: 600, textTransform: 'uppercase', margin: 0 }}>Submitted Total</p>
          <p style={{ fontSize: '1.1rem', fontWeight: 700, color: theme.text, margin: '0.2rem 0 0' }}>₹{submittedTotal.toLocaleString()}</p>
        </div>
        {hasApproved && (
          <div style={{ flex: 1, background: '#F0FDF4', borderRadius: '0.5rem', padding: '0.75rem 1rem', border: `1px solid #BBF7D0` }}>
            <p style={{ fontSize: '0.7rem', color: theme.success, fontWeight: 600, textTransform: 'uppercase', margin: 0 }}>Approved Total</p>
            <p style={{ fontSize: '1.1rem', fontWeight: 700, color: theme.success, margin: '0.2rem 0 0' }}>₹{approvedTotal.toLocaleString()}</p>
          </div>
        )}
      </div>

      {/* History */}
      {history.length > 0 && (
        <>
          <p style={{ fontSize: '0.75rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Activity History</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1rem' }}>
            {history.map((h, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '0.5rem 0.75rem', background: theme.pageBg, borderRadius: '0.375rem', fontSize: '0.8rem' }}>
                <span style={{ flexShrink: 0, marginTop: '1px' }}>
                  {h.action === 'approved' ? '✅' : h.action === 'rejected' ? '❌' : h.action === 'paid' ? '💰' : h.action === 'modified' ? '✏️' : '📝'}
                </span>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{h.action}</span>
                  {h.action_by_name && <span style={{ color: theme.textMuted }}> by {h.action_by_name}</span>}
                  {h.remarks && <span style={{ color: theme.textMuted }}> — {h.remarks}</span>}
                </div>
                <span style={{ color: theme.textMuted, whiteSpace: 'nowrap', fontSize: '0.72rem' }}>{h.created_at ? new Date(h.created_at).toLocaleDateString() : ''}</span>
              </div>
            ))}
          </div>
        </>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ padding: '0.6rem 1.25rem', background: theme.white, border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', color: theme.text }}>Close</button>
      </div>
    </Modal>
  );
}

export default ClaimDetailModal;
